import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import BlockEditor from "@/components/BlockEditor";
import Uploader from "@/components/Uploader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const uid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
const isVideo = (u) => /\.(mp4|webm|mov|ogg)$/i.test(u) || /youtu\.?be|vimeo/.test(u);
const empty = () => ({ title: "", excerpt: "", cover_image: "", blocks: [], featured: false, published: true });

const migrate = (o) => {
  if (Array.isArray(o.blocks) && o.blocks.length) return { ...empty(), ...o };
  const blocks = [];
  if (o.content) blocks.push({ id: uid(), type: "text", html: o.content });
  (o.media || []).forEach((m) => {
    if (isVideo(m)) blocks.push({ id: uid(), type: "video", url: m });
    else blocks.push({ id: uid(), type: "image", url: m, caption: "", align: "center", width: 100 });
  });
  return { ...empty(), ...o, blocks };
};

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = id === "nuevo" || !id;
  const nav = useNavigate();
  const [form, setForm] = useState(empty());
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isNew) {
      api.get(`/articles/${id}`).then((r) => { setForm(migrate(r.data)); setLoading(false); }).catch(() => { toast.error("No se pudo cargar"); setLoading(false); });
    }
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    const payload = { title: form.title, excerpt: form.excerpt, cover_image: form.cover_image, blocks: form.blocks, featured: form.featured, published: form.published };
    try {
      if (isNew) await api.post("/articles", payload);
      else await api.put(`/articles/${id}`, payload);
      toast.success("Artículo guardado");
      nav("/noticiero");
    } catch { toast.error("No se pudo guardar"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="animate-spin text-nasa-blue" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-5 lg:px-8 pt-24 pb-10">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-ink-500 hover:text-nasa-blue text-sm font-semibold mb-6"><ArrowLeft size={16} /> Volver</button>

      <div className="flex items-center justify-between gap-4 mb-8 pb-4 border-b-2 border-paper-200">
        <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-nasa-blue">{isNew ? "Nuevo artículo" : "Editar artículo"}</h1>
        <Button onClick={save} disabled={saving} data-testid="save-article" className="bg-nasa-blue text-white hover:bg-nasa-navy font-semibold rounded-full">
          {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} className="mr-2" /> Guardar</>}
        </Button>
      </div>

      <div className="space-y-6">
        <div>
          <label className="eyebrow text-ink-500 block mb-2">Titular *</label>
          <Input data-testid="article-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="text-lg" placeholder="Titular de la noticia" />
        </div>

        <div>
          <label className="eyebrow text-ink-500 block mb-2">Entradilla</label>
          <Textarea data-testid="article-excerpt" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} placeholder="Resumen breve del artículo" />
        </div>

        <div>
          <label className="eyebrow text-ink-500 block mb-2">Imagen de portada</label>
          <Uploader value={form.cover_image} onChange={(v) => set("cover_image", v)} testid="article-cover" />
        </div>

        <div>
          <label className="eyebrow text-nasa-blue block mb-3">Contenido del artículo</label>
          <p className="text-xs text-ink-400 mb-3">Añade y reordena bloques de texto, imágenes, vídeos y presentaciones. Arrastra con ⠿ o usa las flechas ↑↓.</p>
          <BlockEditor value={form.blocks} onChange={(b) => set("blocks", b)} />
        </div>

        <div className="flex flex-wrap items-center gap-8 bg-paper-50 border border-paper-200 p-5">
          <div className="flex items-center gap-3">
            <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="article-featured" />
            <span className="text-sm font-medium">Destacado (portada)</span>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="article-published" />
            <span className="text-sm font-medium">{form.published ? "Publicado" : "Borrador"}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
